import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Pagination } from "antd";
import { RightOutlined } from "@ant-design/icons";
import Service from "../../service";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import usePublicBranding from "./usePublicBranding";
import PublicCareerLayout from "./PublicCareerLayout";
import JobMeta from "./JobMeta";

const PAGE_SIZE = 10;

/**
 * Public, unauthenticated career page - lists all currently-published, open
 * jobs for a company. No login, no sidebar/topbar chrome.
 */
const CareerPageJobList = () => {
  const { companyslug } = useParams();
  const history = useHistory();
  const { branding } = usePublicBranding(companyslug);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.publicJobList(companyslug),
        body: { page, limit: PAGE_SIZE },
      });
      if (response?.data?.status === 200) {
        setJobs(response.data.data || []);
        setTotalCount(response.data.metadata?.totalFilteredCount || 0);
      } else {
        setError(response?.data?.message || "Unable to load jobs right now.");
      }
    } catch (err) {
      setError("Unable to load jobs right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [companyslug, page]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const openJob = (job) => history.push(`/${companyslug}/careers/${job.public_slug || job._id}`);

  let content;
  if (loading) {
    content = (
      <div className="career-state">
        <Spin size="large" />
      </div>
    );
  } else if (error) {
    content = <div className="career-state career-state-error">{error}</div>;
  } else if (jobs.length === 0) {
    content = (
      <div className="career-state">
        <NodataFoundIcon />
        <p>There are no open positions right now. Please check back later.</p>
      </div>
    );
  } else {
    content = (
      <>
        <div className="career-job-list">
          {jobs.map((job) => (
            <button type="button" className="career-job-card" key={job._id} onClick={() => openJob(job)}>
              <div className="career-job-card-body">
                <div className="career-job-card-title">{job.title}</div>
                <JobMeta job={job} />
              </div>
              <RightOutlined className="career-job-card-arrow" />
            </button>
          ))}
        </div>
        {totalCount > PAGE_SIZE ? (
          <div className="career-pagination">
            <Pagination current={page} pageSize={PAGE_SIZE} total={totalCount} onChange={setPage} showSizeChanger={false} />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <PublicCareerLayout
      companyslug={companyslug}
      branding={branding}
      title="Current Openings"
      hero={
        <>
          <h1 className="career-hero-title">Current Openings</h1>
          <p className="career-hero-subtitle">Browse our open positions and apply directly. No account needed.</p>
        </>
      }
    >
      {content}
    </PublicCareerLayout>
  );
};

export default CareerPageJobList;
